/**
 * condition-builder.tsx — Trình dựng điều kiện trên biến của danh mục biến (khách / thuộc tính AI / hệ thống).
 * Dùng cho "Tự động ngừng trả lời" và "Chuyển theo thuộc tính". Khớp MỘT điều kiện là kích hoạt.
 */
import { Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select'
import { TagInput } from './train-ui'
import { useVariableCatalog, withLiveAttributes, type CatalogVariable } from './train-api'
import { OP_LABELS, conditionProblem, type CollectAttribute, type Condition, type ConditionOp } from './bot-settings'

export function ConditionBuilder({ botId, conditions, onChange, attributes, disabled, newCondition, max = 20 }: {
  botId: string
  conditions: Condition[]
  onChange: (c: Condition[]) => void
  attributes: CollectAttribute[]
  disabled?: boolean
  /** Điều kiện thêm mới mặc định. */
  newCondition: Condition
  max?: number
}) {
  const { t } = useTranslation('aiTrainUi')
  const { data } = useVariableCatalog(botId)
  const cat = withLiveAttributes(data, attributes)
  const groups = (cat?.groups ?? []).map((g) => ({ ...g, variables: g.variables.filter((v) => v.conditionable !== false) })).filter((g) => g.variables.length)
  const all = groups.flatMap((g) => g.variables)
  const find = (id: string) => all.find((v) => v.id === id)
  const upd = (i: number, patch: Partial<Condition>) => onChange(conditions.map((c, j) => (j === i ? { ...c, ...patch } : c)))

  return (
    <div className="space-y-2">
      {conditions.length === 0 && <p className="text-xs text-muted-foreground">{t('conditionBuilder.empty')}</p>}
      {conditions.map((c, i) => {
        const v = find(c.variable)
        const ops = v?.operators ?? (Object.keys(OP_LABELS) as ConditionOp[])
        const problem = conditionProblem(c)
        return (
          <div key={i} className="space-y-1 rounded-lg border p-2">
            <div className="flex flex-wrap items-center gap-2">
              {i > 0 && <span className="text-[11px] font-medium text-muted-foreground">{t('conditionBuilder.or')}</span>}
              <Select value={c.variable} onValueChange={(id) => {
                const nv = find(id)
                const op = nv && !nv.operators.includes(c.op) ? nv.operators[0] : c.op
                upd(i, { variable: id, op, value: defaultValue(op) })
              }} disabled={disabled}>
                <SelectTrigger className="h-8 w-52"><SelectValue placeholder={cat ? t('conditionBuilder.pickVariable') : t('conditionBuilder.loading')}>{v?.name ?? c.variable}</SelectValue></SelectTrigger>
                <SelectContent>
                  {groups.map((g) => (
                    <SelectGroup key={g.id}>
                      <SelectLabel>{g.label}</SelectLabel>
                      {g.variables.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
              <Select value={c.op} onValueChange={(op) => upd(i, { op: op as ConditionOp, value: defaultValue(op as ConditionOp, c.value) })} disabled={disabled}>
                <SelectTrigger className="h-8 w-44"><SelectValue /></SelectTrigger>
                <SelectContent>{ops.map((o) => <SelectItem key={o} value={o}>{OP_LABELS[o]}</SelectItem>)}</SelectContent>
              </Select>
              <ValueInput variable={v} condition={c} onChange={(value) => upd(i, { value })} disabled={disabled} />
              <Button size="icon" variant="ghost" className="ml-auto h-7 w-7" onClick={() => onChange(conditions.filter((_, j) => j !== i))} disabled={disabled} aria-label={t('conditionBuilder.remove')}>
                <Trash2 className="h-3.5 w-3.5 text-destructive" />
              </Button>
            </div>
            {problem && <p className="text-[11px] text-warning">{t('conditionBuilder.problem', { problem })}</p>}
          </div>
        )
      })}
      <Button size="sm" variant="outline" disabled={disabled || conditions.length >= max} onClick={() => onChange([...conditions, { ...newCondition }])}>
        <Plus className="h-3.5 w-3.5" /> {t('conditionBuilder.add')}
      </Button>
    </div>
  )
}

function defaultValue(op: ConditionOp, prev?: Condition['value']): Condition['value'] {
  if (op === 'is_null' || op === 'is_not_null') return undefined
  if (op === 'in' || op === 'not_in') return Array.isArray(prev) ? prev : prev != null && prev !== '' ? [String(prev)] : []
  if (Array.isArray(prev)) return prev[0] ?? ''
  return prev ?? ''
}

function ValueInput({ variable, condition, onChange, disabled }: {
  variable: CatalogVariable | undefined
  condition: Condition
  onChange: (v: Condition['value']) => void
  disabled?: boolean
}) {
  const { t } = useTranslation('aiTrainUi')
  const { op, value } = condition
  if (op === 'is_null' || op === 'is_not_null') return null
  const options = variable?.options ?? []
  if (op === 'in' || op === 'not_in') {
    const list = Array.isArray(value) ? value : []
    return (
      <div className="min-w-[220px] flex-1">
        <TagInput value={list} onChange={(v) => onChange(v)} placeholder={options.length ? t('conditionBuilder.example', { values: options.slice(0, 2).map((o) => o.value).join(', ') }) : t('conditionBuilder.typeValue')} disabled={disabled} max={50} />
      </div>
    )
  }
  if (options.length && variable?.type !== 'number') {
    return (
      <Select value={String(value ?? '')} onValueChange={(v) => onChange(v)} disabled={disabled}>
        <SelectTrigger className="h-8 w-44"><SelectValue placeholder={t('conditionBuilder.value')} /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    )
  }
  const numeric = variable?.type === 'number' || ['>', '<', '>=', '<='].includes(op)
  return (
    <Input
      className="h-8 w-40"
      type={numeric ? 'number' : 'text'}
      value={value == null || Array.isArray(value) ? '' : String(value)}
      onChange={(e) => onChange(numeric && e.target.value !== '' ? Number(e.target.value) : e.target.value)}
      placeholder={t('conditionBuilder.value')}
      disabled={disabled}
    />
  )
}
